import React, { useEffect } from 'react';
import {
    Box,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { Checkbox, Select, H4 } from '@tviklax/ui-components';
import { SellerRegistrationFormData } from '../../components';

interface SellerInformationFormProps {
    formData: SellerRegistrationFormData;
    setFormData: React.Dispatch<React.SetStateAction<SellerRegistrationFormData>>;
    handleChange: (e: any) => void;
    handleNext: () => void;
    loading?: boolean;
    formId: string;
    setNextDisabled?: (disabled: boolean) => void;
}

const SellerInformationForm: React.FC<SellerInformationFormProps> = ({
    formData,
    setFormData,
    handleChange,
    handleNext,
    loading = false,
    formId,
    setNextDisabled,
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: "all",
        defaultValues: formData,
    });

    useEffect(() => {
        setNextDisabled?.(loading);
    }, [loading, setNextDisabled]);

    const onSubmit = () => {
        if (loading) return;
        handleNext();
    };

    return (
        <form id={formId} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ pb: 1, pt: 1 }}>
                    <H4 style={{ fontWeight: 600 }}>Seller Information</H4>
                </Box>

                <Box>
                    <Controller
                        name="sellerType"
                        control={control}
                        defaultValue={formData.sellerType}
                        rules={{ required: "Seller type is required" }}
                        render={({ field: { onChange, value, ref } }) => (
                            <Select
                                ref={ref}
                                label="Seller Type"
                                name="sellerType"
                                value={value || ""}
                                error={errors.sellerType?.message as string}
                                items={[
                                    { label: 'Seller', value: '1' },
                                    { label: 'Direct Manufacturer', value: '2' },
                                    { label: 'Direct Farmers', value: '3' },
                                ]}
                                onChange={(e) => {
                                    handleChange(e);
                                    onChange(e);
                                    if (e.target.value == '1') {
                                        setFormData((prev) => ({
                                            ...prev,
                                            isRetail: true,
                                            isWholesale: false,
                                        }));
                                    } else if (e.target.value == '2' || e.target.value == '3') {
                                        setFormData((prev) => ({
                                            ...prev,
                                            isRetail: false,
                                            isWholesale: true,
                                        }));
                                    }
                                }}
                            />
                        )}
                    />
                </Box>

                <Box sx={{ mb: 5 }}>
                    <Box
                        sx={{
                            mt: -1.5,
                            pl: 2,
                            pb: 0.5,
                            border: "none !important",
                            boxShadow: "none !important",
                        }}
                    >
                        <H4 style={{ fontWeight: 600, marginBottom: '1rem' }}>Selling Option</H4>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Controller
                                name="isRetail"
                                control={control}
                                defaultValue={formData.isRetail}
                                rules={{
                                    validate: () =>
                                        formData.isRetail ||
                                        formData.isWholesale ||
                                        "At least one option must be selected",
                                }}
                                render={({ field: { onChange } }) => (
                                    <Checkbox
                                        name="isRetail"
                                        checked={formData.isRetail}
                                        onChange={(e) => {
                                            handleChange(e);
                                            onChange(e);
                                        }}
                                        label={
                                            <Box component="span" sx={{ ml: 1 }}>
                                                <Box
                                                    component="span"
                                                    sx={{ fontWeight: 500, display: 'block' }}
                                                >
                                                    Retail
                                                </Box>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'block',
                                                        color: '#64748b',
                                                        fontWeight: 500,
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    Sell products directly to consumers
                                                </Box>
                                            </Box>
                                        }
                                    />
                                )}
                            />



                            <Controller
                                name="isWholesale"
                                control={control}
                                defaultValue={formData.isWholesale}
                                rules={{
                                    validate: () =>
                                        formData.isRetail ||
                                        formData.isWholesale ||
                                        "At least one option must be selected",
                                }}
                                render={({ field: { onChange } }) => (
                                    <Checkbox
                                        name="isWholesale"
                                        checked={formData.isWholesale}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            handleChange(e);
                                            onChange(e);
                                        }}
                                        label={
                                            <Box component="span" sx={{ ml: 1 }}>
                                                <Box
                                                    component="span"
                                                    sx={{ fontWeight: 500, display: 'block' }}
                                                >
                                                    Wholesale
                                                </Box>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'block',
                                                        color: '#64748b',
                                                        fontWeight: 500,
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    Sell products in bulk to other businesses/consumers
                                                </Box>
                                            </Box>
                                        }
                                    />
                                )}
                            />
                        </Box>

                        {(errors.isRetail || errors.isWholesale) && (
                            <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                                {errors.isRetail?.message || errors.isWholesale?.message}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </form>
    );
};

export default SellerInformationForm;
